import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, Send, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  { value: "event", label: "Event Child Care (Wedding, Gala, Party)" },
  { value: "travel", label: "Travel & Hospitality Care (Hotel, Villa)" },
  { value: "corporate", label: "Corporate & Backup Care" },
  { value: "other", label: "Something Else" },
];

const initial = {
  full_name: "",
  email: "",
  phone: "",
  service_type: "",
  service_other_detail: "",
  event_date: null,
  location: "",
  children_ages: "",
  number_of_children: 1,
  notes: "",
};

const InquiryForm = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.full_name.trim() || form.full_name.trim().length < 2)
      return "Please enter your full name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email))
      return "Please enter a valid email address.";
    if (!form.phone.trim() || form.phone.trim().length < 5)
      return "Please enter a valid phone number.";
    if (!form.service_type) return "Please select a service type.";
    if (form.service_type === "other" && !form.service_other_detail.trim())
      return "Please describe the service you're looking for.";
    if (!form.event_date) return "Please select an event date.";
    if (!form.location.trim()) return "Please enter a location.";
    if (!form.children_ages.trim())
      return "Please share your children's ages.";
    if (!form.number_of_children || form.number_of_children < 1)
      return "Please enter the number of children.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    try {
      setSubmitting(true);
      const otherDetail = form.service_type === "other" ? form.service_other_detail.trim() : "";
      const notesCombined = otherDetail
        ? `Service description: ${otherDetail}${form.notes ? `\n\n${form.notes}` : ""}`
        : form.notes;
      const payload = {
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        service_type: form.service_type,
        location: form.location,
        children_ages: form.children_ages,
        number_of_children: Number(form.number_of_children),
        notes: notesCombined,
        event_date: format(form.event_date, "yyyy-MM-dd"),
      };
      await axios.post(`${API}/inquiries`, payload);
      setSuccess(true);
      setForm(initial);
      toast.success("Inquiry received. Our concierge will reach out shortly.");
    } catch (e2) {
      const msg =
        e2?.response?.data?.detail?.toString() ||
        "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="inquire"
      data-testid="inquiry-section"
      className="relative py-24 lg:py-32"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-2">
            <div className="text-xs tracking-[0.25em] uppercase text-primary/80">
              Book Care
            </div>
            <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl leading-[1.1] tracking-tight text-foreground">
              Tell us about your family.
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Share a few details and our concierge team will reply within one
              business day to match you with a caregiver, transparent pricing,
              and next steps.
            </p>

            <ul className="mt-8 space-y-3 text-sm">
              {[
                "Response within one business day",
                "No obligation, fully confidential",
                "Available for single-day or multi-day engagements",
              ].map((t) => (
                <li
                  key={t}
                  className="flex items-start gap-3 text-muted-foreground"
                >
                  <CheckCircle2
                    size={16}
                    className="text-primary mt-0.5 shrink-0"
                  />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <form
              onSubmit={onSubmit}
              data-testid="inquiry-form"
              className="relative rounded-2xl border border-border/70 bg-card p-6 md:p-10 shadow-[0_20px_60px_-30px_rgba(63,109,138,0.28)]"
            >
              {success && (
                <div
                  data-testid="inquiry-success"
                  className="mb-6 flex items-start gap-3 rounded-xl bg-primary/5 border border-primary/20 p-4 text-sm text-primary"
                >
                  <CheckCircle2 size={18} className="mt-0.5" />
                  <div>
                    <div className="font-medium">Inquiry received</div>
                    <div className="text-primary/80">
                      A member of our concierge team will be in touch within
                      one business day.
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Full name" htmlFor="full_name">
                  <Input
                    id="full_name"
                    data-testid="input-full-name"
                    value={form.full_name}
                    onChange={(e) => update("full_name", e.target.value)}
                    placeholder="Jane Delacroix"
                    className="h-11 rounded-lg"
                  />
                </Field>
                <Field label="Email" htmlFor="email">
                  <Input
                    id="email"
                    type="email"
                    data-testid="input-email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="jane@family.com"
                    className="h-11 rounded-lg"
                  />
                </Field>
                <Field label="Phone" htmlFor="phone">
                  <Input
                    id="phone"
                    data-testid="input-phone"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    className="h-11 rounded-lg"
                  />
                </Field>
                <Field label="Service type" htmlFor="service_type">
                  <Select
                    value={form.service_type}
                    onValueChange={(v) => update("service_type", v)}
                  >
                    <SelectTrigger
                      id="service_type"
                      data-testid="select-service-type"
                      className="h-11 rounded-lg"
                    >
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((s) => (
                        <SelectItem
                          key={s.value}
                          value={s.value}
                          data-testid={`option-${s.value}`}
                        >
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                {form.service_type === "other" && (
                  <div className="md:col-span-2">
                    <Field label="Describe the service you're looking for" htmlFor="service_other_detail">
                      <Textarea
                        id="service_other_detail"
                        data-testid="input-service-other-detail"
                        value={form.service_other_detail}
                        onChange={(e) => update("service_other_detail", e.target.value)}
                        placeholder="e.g. Overnight care during a yacht charter, sibling group at a private ski chalet, on-set film crew childcare..."
                        rows={3}
                        className="rounded-lg resize-none"
                      />
                    </Field>
                  </div>
                )}

                <Field label="Event date" htmlFor="event_date">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        id="event_date"
                        data-testid="button-event-date"
                        variant="outline"
                        type="button"
                        className={cn(
                          "h-11 rounded-lg w-full justify-start text-left font-normal",
                          !form.event_date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon size={16} className="mr-2" />
                        {form.event_date
                          ? format(form.event_date, "PPP")
                          : "Select a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0"
                      align="start"
                      data-testid="calendar-popover"
                    >
                      <Calendar
                        mode="single"
                        selected={form.event_date}
                        onSelect={(d) => update("event_date", d)}
                        disabled={(d) =>
                          d < new Date(new Date().setHours(0, 0, 0, 0))
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </Field>

                <Field
                  label="Number of children"
                  htmlFor="number_of_children"
                >
                  <Input
                    id="number_of_children"
                    data-testid="input-number-children"
                    type="number"
                    min={1}
                    max={20}
                    value={form.number_of_children}
                    onChange={(e) =>
                      update("number_of_children", e.target.value)
                    }
                    className="h-11 rounded-lg"
                  />
                </Field>

                <div className="md:col-span-2">
                  <Field label="Location / Venue" htmlFor="location">
                    <Input
                      id="location"
                      data-testid="input-location"
                      value={form.location}
                      onChange={(e) => update("location", e.target.value)}
                      placeholder="The Ritz-Carlton, New York"
                      className="h-11 rounded-lg"
                    />
                  </Field>
                </div>

                <div className="md:col-span-2">
                  <Field label="Children's ages" htmlFor="children_ages">
                    <Input
                      id="children_ages"
                      data-testid="input-children-ages"
                      value={form.children_ages}
                      onChange={(e) =>
                        update("children_ages", e.target.value)
                      }
                      placeholder="e.g. 6 months, 3 years, 5 years"
                      className="h-11 rounded-lg"
                    />
                  </Field>
                </div>

                <div className="md:col-span-2">
                  <Field label="Anything else we should know?" htmlFor="notes">
                    <Textarea
                      id="notes"
                      data-testid="input-notes"
                      value={form.notes}
                      onChange={(e) => update("notes", e.target.value)}
                      placeholder="Allergies, preferences, schedule, dietary notes..."
                      rows={4}
                      className="rounded-lg resize-none"
                    />
                  </Field>
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="text-xs text-muted-foreground">
                  By submitting, you agree to be contacted by our concierge
                  team. We never share your information.
                </p>
                <Button
                  type="submit"
                  data-testid="submit-inquiry"
                  disabled={submitting}
                  className="rounded-full h-12 px-8 bg-primary hover:bg-primary/90 text-primary-foreground min-w-[180px]"
                >
                  {submitting ? (
                    "Sending..."
                  ) : (
                    <span className="flex items-center gap-2">
                      Send Inquiry
                      <Send size={16} />
                    </span>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, htmlFor, children }) => (
  <div className="flex flex-col gap-2">
    <Label htmlFor={htmlFor} className="text-xs uppercase tracking-widest text-muted-foreground">
      {label}
    </Label>
    {children}
  </div>
);

export default InquiryForm;
